import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from 'react';
import { isAxiosError } from 'axios';
import { saveLiveCorrections } from '@utils/api/realtime/live-transcript.ts';
import {
  acceptLiveSpeakerSave,
  LiveDraft
} from '@utils/editors/live-transcript-state.ts';

type ErrorState = 'load' | 'save' | 'conflict' | 'connection' | null;
type Props = {
  sessionId: string;
  scope: string;
  canEdit: boolean;
  generation: MutableRefObject<number>;
  rows: MutableRefObject<LiveDraft[]>;
  saving: MutableRefObject<number | null>;
  commit: (update: (current: LiveDraft[]) => LiveDraft[]) => void;
  setSaving: Dispatch<SetStateAction<boolean>>;
  setError: Dispatch<SetStateAction<ErrorState>>;
  onFailure: () => void;
};

const delay = 700;

export const useLiveSpeakerAutosave = ({
  sessionId,
  scope,
  canEdit,
  generation,
  rows,
  saving,
  commit,
  setSaving,
  setError,
  onFailure
}: Props) => {
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const failed = useRef(new Set<string>());
  const editable = useRef(canEdit);
  const speakerError = useRef(false);
  const clearError = useCallback(() => {
    if (!speakerError.current) return;
    speakerError.current = false;
    setError(current =>
      current === 'save' || current === 'conflict' ? null : current
    );
  }, [setError]);
  const cancel = useCallback((ids?: string[]) => {
    const selected = ids ? new Set(ids) : null;
    for (const [id, timer] of timers.current) {
      if (!selected || selected.has(id)) {
        clearTimeout(timer);
        timers.current.delete(id);
      }
    }
  }, []);
  const save = useCallback(
    async (
      id: string,
      expectedGeneration = generation.current
    ): Promise<boolean> => {
      cancel([id]);
      if (generation.current !== expectedGeneration) return false;
      if (!editable.current) return false;
      if (saving.current !== null) {
        const active = generation.current;
        timers.current.set(
          id,
          setTimeout(() => {
            if (generation.current === active) void save(id, active);
          }, 100)
        );
        return false;
      }
      const row = rows.current.find(item => item.segment.segment_id === id);
      if (!row?.speakerDirty || row.speakerEditVersion === undefined) {
        failed.current.delete(id);
        if (!failed.current.size) clearError();
        return true;
      }
      const base = row.serverSegment ?? row.segment;
      const active = generation.current;
      const version = row.speakerEditVersion;
      saving.current = active;
      setSaving(true);
      try {
        const saved = await saveLiveCorrections(sessionId, [
          {
            segment_id: id,
            revision: base.revision,
            utterance: {
              ...base.utterance,
              speaker: row.segment.utterance.speaker
            }
          }
        ]);
        if (generation.current !== active) return false;
        const server = saved.updated.find(segment => segment.segment_id === id);
        if (!server) throw new Error('missing_speaker_segment');
        commit(current => acceptLiveSpeakerSave(current, id, version, server));
        failed.current.delete(id);
        if (!failed.current.size) clearError();
        return true;
      } catch (failure) {
        if (generation.current === active) {
          failed.current.add(id);
          speakerError.current = true;
          onFailure();
          setError(
            isAxiosError(failure) && failure.response?.status === 409
              ? 'conflict'
              : 'save'
          );
        }
        return false;
      } finally {
        if (saving.current === active) saving.current = null;
        if (generation.current === active) setSaving(false);
      }
    },
    [
      cancel,
      clearError,
      commit,
      generation,
      onFailure,
      rows,
      saving,
      sessionId,
      setError,
      setSaving
    ]
  );
  const schedule = useCallback(
    (id: string) => {
      cancel([id]);
      const active = generation.current;
      timers.current.set(
        id,
        setTimeout(() => {
          if (generation.current === active) void save(id, active);
        }, delay)
      );
    },
    [cancel, save]
  );
  const retry = useCallback(async () => {
    const ids = [...failed.current];
    if (!ids.length) return false;
    const active = generation.current;
    let saved = true;
    for (const id of ids) {
      if (generation.current !== active) return false;
      saved = (await save(id, active)) && saved;
    }
    return saved;
  }, [generation, save]);
  const acknowledge = useCallback((ids: string[]) => {
    ids.forEach(id => failed.current.delete(id));
  }, []);
  useEffect(() => {
    editable.current = canEdit;
    if (!canEdit) cancel();
  }, [canEdit, cancel]);
  useEffect(() => {
    failed.current.clear();
    speakerError.current = false;
    return () => cancel();
  }, [cancel, scope]);
  return useMemo(
    () => ({ acknowledge, cancel, clearError, retry, schedule }),
    [acknowledge, cancel, clearError, retry, schedule]
  );
};
